// src/utils/api/validation.ts
//Locked

import { ValidationError } from './errors';
import {
    ClothData,
    WeightOption,
    GradeOption,
    WindAverageOption,
    SailUseOption,
    BattenOptionNew,
    QuoteData,
    LPUpdateResponse,
    QuoteCreateResponse
} from '../../types/quoter/types'

type ValidationRule<T> = {
    validate: (value: unknown) => value is T;
    message: string;
};

export class Validator<T> {
    private rules: ValidationRule<T>[];

    constructor(rules: ValidationRule<T>[]) {
        this.rules = rules;
    }

    validate(value: unknown): T {
        const errors: ValidationError[] = [];

        for (const rule of this.rules) {
            if (!rule.validate(value)) {
                errors.push({
                    field: 'response',
                    message: rule.message,
                    code: 'INVALID_FORMAT',
                    value
                });
            }
        }

        if (errors.length > 0) {
            throw errors;
        }

        return value as T;
    }
}

// Type guards for basic validations
export const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

export const isArray = (value: unknown): value is unknown[] =>
    Array.isArray(value);

export const isNumber = (value: unknown): value is number =>
    typeof value === 'number' && !isNaN(value);

export const isString = (value: unknown): value is string =>
    typeof value === 'string';

export const isBoolean = (value: unknown): value is boolean =>
    typeof value === 'boolean';

// Complex type validators
export const createObjectValidator = <T>(shape: Record<keyof T, (value: unknown) => boolean>) => {
    return (value: unknown): value is T => {
        if (!isObject(value)) return false;

        return (Object.entries(shape) as Array<[string, (value: unknown) => boolean]>).every(([key, validator]) => {
            return validator(value[key]);
        });
    };
};

// Cart item quantity
export const quantityUpdateValidator = new Validator<QuantityUpdateResponse>([{
    validate: createObjectValidator<QuantityUpdateResponse>({
        success: isBoolean,
        quantity: isNumber,
        message: (v): v is string | undefined => v === undefined || isString(v)
    }),
    message: 'Invalid quantity update response'
}]);

export interface QuantityUpdateResponse {
    success: boolean;
    quantity: number;
    message?: string;
}

export const createArrayValidator = <T>(itemValidator: (value: unknown) => value is T) => {
    return (value: unknown): value is T[] => {
        return isArray(value) && value.every(item => itemValidator(item));
    };
};

// Specific validators for our data types
export const isBoat = createObjectValidator<{
    BoatId: number;
    BoatModel: string;
    Boat_UrlId?: string;  // SEO-friendly URL identifier (optional)
    I: number;
    J: number;
    P: number;
    E: number;
}>({
    BoatId: isNumber,
    BoatModel: isString,
    Boat_UrlId: (v): v is string | undefined => v === undefined || isString(v),
    I: isNumber,
    J: isNumber,
    P: isNumber,
    E: isNumber
});

export const isSail = createObjectValidator<{
    SailId: number;
    SailName: string;
    SailDescription: string;
    SailAreaSqFt: number | null;
    Sail_ImgName: string;
    SailCutId: number;
    SailCutName: string;
    Manufacturer: string | null;
}>({
    SailId: isNumber,
    SailName: isString,
    SailDescription: isString,
    SailAreaSqFt: (v): v is number | null => v === null || isNumber(v),
    Sail_ImgName: isString,
    SailCutId: isNumber,
    SailCutName: isString,
    Manufacturer: (v): v is string | null => v === null || isString(v),
});

// Create validator instances
export const boatListValidator = new Validator([{
    validate: createArrayValidator(isBoat),
    message: 'Invalid boat list format'
}]);

export const sailListValidator = new Validator([{
    validate: createArrayValidator(isSail),
    message: 'Invalid sail list format'
}]);

//-------------------------------------------------


// Validators
export const isClothData = createObjectValidator<ClothData>({
    OverrideClothId: isNumber,
    Manufacturer: isString,
    ClothMakeName: isString,
    WeightOz: isNumber,
    IsLaminate: isNumber,
    PriceDelta: isNumber,
    HasLaminateOption: isNumber
});

export const isWeightOption = createObjectValidator<WeightOption>({
    QuoteClothOverrideWeightId: isNumber,  
    QuoteId: isNumber,                      
    ClothOverrideWeightId: isNumber,        
    WeightDescription: isString,
    PriceDelta: isNumber,
    IsSelected: isNumber,
    IsEnabled: isNumber
});

export const isGradeOption = createObjectValidator<GradeOption>({
    QuoteClothOverrideGradeId: isNumber,   
    QuoteId: isNumber,                      
    ClothOverrideGradeId: isNumber,        
    GradeDescription: isString,
    ClothOverrideDescription: isString,
    PriceDelta: isNumber,
    IsSelected: isNumber,
    IsEnabled: isNumber
});

export const isWindAverageOption = createObjectValidator<WindAverageOption>({
    WindAverageAnswerId: isNumber,
    AnswerText: isString,
    AnswerValue: isNumber,
    IsSelected: isNumber
});

export const isSailUseOption = createObjectValidator<SailUseOption>({
    SailUseAnswerId: isNumber,
    AnswerText: isString,
    AnswerValue: isNumber,
    IsSelected: isNumber
});

//export const isBattenOption = createObjectValidator<BattenOption>({
//    OptionBattenId: isNumber,
//    BattenNumber: isNumber,
//    CostDelta: isNumber,
//    IsFull: isNumber
//});

export const isBattenOptionNew = createObjectValidator<BattenOptionNew>({
    FullBattens: isNumber,
    HalfBattens: isNumber,
    CostDelta: isNumber,
    DisplayText: isString,
    IsSelected: isNumber,
    SectionName: isString
});

// Response validators
export const quoteValidator = new Validator<QuoteData>([{
    validate: createObjectValidator<QuoteData>({
        UserId: isNumber,
        BoatId: isNumber,
        SailId: isNumber,
        SailCutId: isNumber,
        IsFurling: isNumber,
        SailWeightTypeId: isNumber,
        SailWeightName: isString,
        SelectedClothId: isNumber,
        AdjustedSquareFeet: isNumber,
        PercentLP: isNumber,
        CalculatedPrice: isNumber,
        DiscountCodeId: (v): v is number | null => v === null || isNumber(v),
        CustomerDiscountPercent: isNumber,
        CustomerDiscountApplied: isNumber,
        ResaleDiscountPercent: isNumber,
        ResaleDiscountApplied: isNumber,
        QuotedPrice: isNumber,
        ResaleUserPrice: isNumber,
        IsEverydaySail: isNumber,
        WindAverageAnswerId: isNumber,
        SailUseAnswerId: isNumber,
        QuoteLeadDays: isNumber,
        Notes: (v): v is string | null => v === null || isString(v),
        CreateTime: isString,
        SailDisplayName: isString,
        Sail_ImgName: isString,
        SelectionSummary: (v): v is string | null => v === null || isString(v),
    }),
    message: 'Invalid quote data format'
}]);

export const clothDataValidator = new Validator<ClothData>([{
    validate: isClothData,
    message: 'Invalid cloth data format'
}]);

export const weightOptionsValidator = new Validator<WeightOption[]>([{
    validate: createArrayValidator(isWeightOption),
    message: 'Invalid cloth options format'
}]);

export const gradeOptionsValidator = new Validator<GradeOption[]>([{
    validate: createArrayValidator(isGradeOption),
    message: 'Invalid grade options format'
}]);

export const windOptionsValidator = new Validator<WindAverageOption[]>([{
    validate: createArrayValidator(isWindAverageOption),
    message: 'Invalid wind options format'
}]);

export const sailUseOptionsValidator = new Validator<SailUseOption[]>([{
    validate: createArrayValidator(isSailUseOption),
    message: 'Invalid sail use options format'
}]);

export const lpUpdateValidator = new Validator<LPUpdateResponse>([{
    validate: createObjectValidator<LPUpdateResponse>({
        success: isBoolean,
        message: (v): v is string | undefined => v === undefined || isString(v),
        updatedLP: (v): v is number | undefined => v === undefined || isNumber(v)
    }),
    message: 'Invalid LP update response'
}]);

export const quoteCreateValidator = new Validator<QuoteCreateResponse>([{
    validate: createObjectValidator<QuoteCreateResponse>({
        quoteId: isNumber
    }),
    message: 'Invalid quote creation response'
}]);





